import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { useValueChurnProbe } from "../utils/loopDebug";
import { LinkRequiredFormPayload, RequiredForm } from "../types/formTemplates";
import { API_CONFIG, getApiUrl } from "../config/api";
import { useSalon } from "./SalonContext";
import { useApp } from "./AppContext";

interface UpdateRequiredFormPayload {
  required?: boolean;
  template_version?: number | null;
}

interface ServiceRequiredFormsContextType {
  isLoading: boolean;
  error: string | null;
  listRequiredForms: (salonServiceId: string) => Promise<RequiredForm[]>;
  linkRequiredForm: (
    salonServiceId: string,
    payload: LinkRequiredFormPayload,
  ) => Promise<RequiredForm>;
  updateRequiredForm: (
    salonServiceId: string,
    requirementId: string,
    payload: UpdateRequiredFormPayload,
  ) => Promise<RequiredForm>;
  unlinkRequiredForm: (
    salonServiceId: string,
    requirementId: string,
  ) => Promise<void>;
}

const ServiceRequiredFormsContext =
  createContext<ServiceRequiredFormsContextType | null>(null);

function extractMessage(err: unknown): string {
  if (err instanceof Error && err.message) return err.message;
  return "An unexpected error occurred";
}

export function ServiceRequiredFormsProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { businessInfo } = useSalon() as any;
  const { fetchWithAuth } = useApp();

  const requireSalon = useCallback((): string => {
    const id = businessInfo?.id;
    if (!id) throw new Error("No salon ID available");
    return id;
  }, [businessInfo?.id]);

  const listRequiredForms = useCallback(
    async (salonServiceId: string): Promise<RequiredForm[]> => {
      const salonId = requireSalon();
      try {
        setIsLoading(true);
        setError(null);
        const response: { required_forms?: RequiredForm[] } | RequiredForm[] =
          await fetchWithAuth(
            getApiUrl(API_CONFIG.ENDPOINTS.SERVICE_REQUIRED_FORMS, "", {
              salonId,
              salonServiceId,
            }),
          );
        if (Array.isArray(response)) return response;
        return response?.required_forms ?? [];
      } catch (err) {
        setError(extractMessage(err));
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [requireSalon, fetchWithAuth],
  );

  const linkRequiredForm = useCallback(
    async (
      salonServiceId: string,
      payload: LinkRequiredFormPayload,
    ): Promise<RequiredForm> => {
      const salonId = requireSalon();
      try {
        setIsLoading(true);
        setError(null);
        const response: RequiredForm = await fetchWithAuth(
          getApiUrl(API_CONFIG.ENDPOINTS.SERVICE_REQUIRED_FORMS, "", {
            salonId,
            salonServiceId,
          }),
          { method: "POST", body: JSON.stringify(payload) },
        );
        return response;
      } catch (err) {
        setError(extractMessage(err));
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [requireSalon, fetchWithAuth],
  );

  const updateRequiredForm = useCallback(
    async (
      salonServiceId: string,
      requirementId: string,
      payload: UpdateRequiredFormPayload,
    ): Promise<RequiredForm> => {
      const salonId = requireSalon();
      try {
        setIsLoading(true);
        setError(null);
        const response: RequiredForm = await fetchWithAuth(
          getApiUrl(API_CONFIG.ENDPOINTS.SERVICE_REQUIRED_FORM_BY_ID, "", {
            salonId,
            salonServiceId,
            requirementId,
          }),
          { method: "PUT", body: JSON.stringify(payload) },
        );
        return response;
      } catch (err) {
        setError(extractMessage(err));
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [requireSalon, fetchWithAuth],
  );

  const unlinkRequiredForm = useCallback(
    async (salonServiceId: string, requirementId: string): Promise<void> => {
      const salonId = requireSalon();
      try {
        setError(null);
        await fetchWithAuth(
          getApiUrl(API_CONFIG.ENDPOINTS.SERVICE_REQUIRED_FORM_BY_ID, "", {
            salonId,
            salonServiceId,
            requirementId,
          }),
          { method: "DELETE" },
        );
      } catch (err) {
        setError(extractMessage(err));
        throw err;
      }
    },
    [requireSalon, fetchWithAuth],
  );

  // [LOOP] churn probe
  useValueChurnProbe("ServiceRequiredForms", { isLoading, error });

  // Memoized so consumers don't re-render on every provider render; every
  // method above is useCallback'd, so the deps are all stable references.
  const value: ServiceRequiredFormsContextType = useMemo(
    () => ({
      isLoading,
      error,
      listRequiredForms,
      linkRequiredForm,
      updateRequiredForm,
      unlinkRequiredForm,
    }),
    [
      isLoading,
      error,
      listRequiredForms,
      linkRequiredForm,
      updateRequiredForm,
      unlinkRequiredForm,
    ],
  );

  return (
    <ServiceRequiredFormsContext.Provider value={value}>
      {children}
    </ServiceRequiredFormsContext.Provider>
  );
}

export function useServiceRequiredForms() {
  const ctx = useContext(ServiceRequiredFormsContext);
  if (!ctx) {
    throw new Error(
      "useServiceRequiredForms must be used within a ServiceRequiredFormsProvider",
    );
  }
  return ctx;
}
